import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const CARD_W = 256;

/**
 * Small "i" affordance for a sidebar item that explains what the tab does.
 *
 * Lives inside the item's NavLink, so it intercepts its own clicks
 * (preventDefault + stopPropagation) — tapping the icon shows the tip instead
 * of navigating. Reveals on hover/focus for pointer + keyboard users, and
 * toggles on tap for touch devices that have no hover.
 *
 * The tooltip renders through a portal to document.body: the sidebar <aside>
 * has overflow-x-hidden AND a transform, which would clip even a fixed-position
 * child, so it must escape that subtree entirely.
 */
export default function SidebarInfoTip({ title, subtitle, description }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const btnRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const show = () => {
        const el = btnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        // Prefer placing to the right of the icon; clamp to the viewport.
        let left = r.right + 8;
        if (left + CARD_W > window.innerWidth - 8) {
            left = Math.max(8, window.innerWidth - CARD_W - 8);
        }
        const top = Math.min(Math.max(8, r.top), window.innerHeight - 170);
        setPos({ top, left });
        setOpen(true);
    };

    const hide = () => setOpen(false);

    // Dismiss on outside click or scroll (covers touch toggling and scrolling).
    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        window.addEventListener('scroll', close, true);
        document.addEventListener('click', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            document.removeEventListener('click', close);
        };
    }, [open]);

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                aria-label={`About ${title}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); open ? hide() : show(); }}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                className={`ml-auto shrink-0 p-0.5 rounded-full transition-colors ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
            >
                <Info size={13} />
            </button>

            {open && createPortal(
                <div
                    role="tooltip"
                    style={{ position: 'fixed', top: pos.top, left: pos.left, width: CARD_W }}
                    onClick={(e) => e.stopPropagation()}
                    className={`z-[60] rounded-lg border p-3 shadow-xl font-rasa text-left ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
                >
                    <p className="text-sm font-semibold leading-tight">{title}</p>
                    {subtitle && (
                        <p className={`text-[11px] font-unna uppercase tracking-wider mb-1.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            {subtitle}
                        </p>
                    )}
                    <p className="text-xs leading-relaxed">{description}</p>
                </div>,
                document.body
            )}
        </>
    );
}
